import type { GridRowModelUpdate } from '@mui/x-data-grid-pro';
import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import {
  toPortfolioEditableFields,
  useTrackPortfolioItemsEdited,
} from '@/lib/analytics/portfolio/useTrackPortfolioItemsEdited';
import type { Item, ItemPatchRequest } from '@/lib/api/types/items';
import { useUpdateItemMutation } from '@/lib/mutations/item/useUpdateItemMutation';
import { commonQueryKeys } from '@/lib/query-keys/common';
import { itemV1ToV2 } from '@/lib/utils/itemConvert';
import { TableCellMutationContext } from '../context/TableCellMutationContext';
import { useVisibleItems } from '../context/VisibleItemsContext';
import type { ItemsTableRow } from '../types';

export type CommitParams = {
  itemId: number;
  api: { updateRows: (updates: GridRowModelUpdate[]) => void };
  row: ItemsTableRow;
  data: ItemPatchRequest;
};

const useItemStoreCellMutation = () => {
  const queryClient = useQueryClient();
  const { reconcileItemAfterMutation } = useVisibleItems();
  const trackPortfolioItemsEdited = useTrackPortfolioItemsEdited();
  const { mutate, isPending } = useUpdateItemMutation({
    onSuccess: (updatedItem, variables) => {
      reconcileItemAfterMutation(updatedItem.id, itemV1ToV2(updatedItem));
      // Refresh any observer of the canonical per-item key (e.g. an open item
      // panel via useItemFetchQuery); the refetched value flows back into the
      // page cache through useCrossCachePagesSync. No-op when nothing observes it.
      void queryClient.invalidateQueries({ queryKey: commonQueryKeys.item(updatedItem.id) });
      trackPortfolioItemsEdited({
        changedFields: toPortfolioEditableFields(Object.keys(variables?.data ?? {})),
        itemIds: [updatedItem.id],
        isBulkEdit: false,
        itemPanel: false,
      });
    },
  });
  return { mutate, isPending };
};

export const useTableCellMutation = () => {
  const queryClient = useQueryClient();
  const { patchItem, setItem, instanceKey } = useVisibleItems();
  const ctx = React.useContext(TableCellMutationContext);
  const itemStore = useItemStoreCellMutation();
  const [isExternalPending, setIsExternalPending] = React.useState(false);

  const commit = async ({ itemId, api, row, data }: CommitParams) => {
    const previousRow = { ...row };
    const previousItem = patchItem(itemId, data as Partial<Item>);
    api.updateRows([{ id: itemId, ...data }]);

    await queryClient.cancelQueries(
      { queryKey: [commonQueryKeys.itemsTable.visibleItems, instanceKey] },
      { revert: false },
    );

    const onError = () => {
      if (previousItem) {
        setItem(previousItem);
      }
      const rollback: GridRowModelUpdate = { id: itemId };
      for (const key of Object.keys(data) as (keyof typeof data)[]) {
        rollback[key] = (previousRow as Record<string, unknown>)[key] ?? null;
      }
      api.updateRows([rollback]);
    };

    if (ctx) {
      setIsExternalPending(true);
      ctx.commit(itemId, data, {
        onSuccess: () => setIsExternalPending(false),
        onError: () => {
          setIsExternalPending(false);
          onError();
        },
      });
    } else {
      itemStore.mutate({ itemId, data }, { onError });
    }
  };

  return { commit, isPending: ctx ? isExternalPending : itemStore.isPending };
};
